import React from "react"
import { Box, Stack, type StackProps } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { Heading, ViraText } from "../atoms/ViraText"

export type SectionHeaderProps = StackProps & {
  title: string
  subtitle?: string
  eyebrow?: string
  level?: "page" | "section" | "card"
  actions?: React.ReactNode
}

export function SectionHeader({
  title,
  subtitle,
  eyebrow,
  level = "section",
  actions,
  sx,
  ...rest
}: SectionHeaderProps) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
      spacing={{ xs: 1.5, sm: 2 }}
      sx={composeSx(
        () => ({
          mb: level === "page" ? 3 : 2,
          width: "100%",
          maxWidth: "100%",
        }),
        sx,
      )}
      {...rest}
    >
      <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
        {eyebrow ? (
          <Heading level="eyebrow" sx={{ mb: 0.75 }}>
            {eyebrow}
          </Heading>
        ) : null}
        <Heading level={level}>{title}</Heading>
        {subtitle ? (
          <ViraText tone="subtle" sx={{ fontSize: 12, mt: 0.25 }}>
            {subtitle}
          </ViraText>
        ) : null}
      </Box>
      {actions ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            maxWidth: "100%",
            "& > *": { maxWidth: "100%" },
          }}
        >
          {actions}
        </Stack>
      ) : null}
    </Stack>
  )
}

export type CardHeaderProps = SectionHeaderProps

export function CardHeader(props: CardHeaderProps) {
  return <SectionHeader level="card" {...props} />
}
